import React, { useState } from 'react';
import { M } from 'design-system-web';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { getClient } from 'lane-shared/apollo';
import { bulkResetPasswordEmails } from 'lane-shared/graphql/user';
import { ANALYTIC_KEYS } from 'constants-analytics';
import { useSimpleTrack } from 'lane-shared/hooks/useSimpleTrack';
import useUserDataContext from 'lane-shared/hooks/useUserDataContext';
import { useChannelAdminContext } from 'hooks';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  bulkSelectedUsers: {
    id: string;
    email: string;
  }[];
  onSuccess?: () => void;
};

const TRANSLATION_KEYS = {
  modalTitle: 'web.admin.channel.users.all.table.bulkResetPassword.modal.title',
  confirmButtonLabel:
    'web.admin.channel.users.all.table.bulkResetPassword.modal.confirmButton.label',
  cancelButtonLabel:
    'web.admin.channel.users.all.table.bulkResetPassword.modal.cancelButton.label',
  promptText:
    'web.admin.channel.users.all.table.bulkResetPassword.modal.prompt.text',
  messageText:
    'web.admin.channel.users.all.table.bulkResetPassword.modal.message.text',
  toastText:
    'web.admin.channel.users.all.table.bulkResetPassword.success.toast.text',
  errorMessage:
    'web.admin.channel.users.all.table.bulkResetPassword.failed.alert.text',
};

export const BulkResetPasswordModal = ({
  isOpen,
  onClose,
  bulkSelectedUsers,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { channel } = useChannelAdminContext();
  const { user: adminUser } = useUserDataContext();
  const simpleTrack = useSimpleTrack();

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const emails = bulkSelectedUsers.map(({ email }) => email);

      await getClient().mutate({
        mutation: bulkResetPasswordEmails,
        variables: {
          emails,
          channelId: channel?._id,
        },
      });

      window.Toast.show(t(TRANSLATION_KEYS.toastText), 5000);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.errorMessage),
        error: err,
      });
    }

    simpleTrack(
      ANALYTIC_KEYS.ANALYTIC_USER_MANAGEMENT_BULK_RESET_PASSWORD_CONFIRM,
      {
        channelId: channel?._id,
        channelName: channel?.name,
        me: adminUser?._id,
        userIds: bulkSelectedUsers.map(({ id }) => id),
      }
    );

    setLoading(false);
    onClose();
  };

  const handleClose = async () => {
    simpleTrack(
      ANALYTIC_KEYS.ANALYTIC_USER_MANAGEMENT_BULK_RESET_PASSWORD_CANCEL,
      {
        channelId: channel?._id,
        channelName: channel?.name,
        me: adminUser?._id,
        userIds: bulkSelectedUsers.map(({ id }) => id),
      }
    );

    onClose();
  };

  const bulkSelectedUsersCount = bulkSelectedUsers.length;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleClose}
      handleConfirm={handleConfirm}
      title={t(TRANSLATION_KEYS.modalTitle)}
      confirmText={t(TRANSLATION_KEYS.confirmButtonLabel)}
      cancelText={t(TRANSLATION_KEYS.cancelButtonLabel)}
      loading={loading}
      confirmActionDisabled={loading}
      cancelActionDisabled={loading}
    >
      <M>
        <Trans
          components={[<b />]}
          i18nKey={TRANSLATION_KEYS.promptText}
          count={bulkSelectedUsersCount}
          values={{
            count: bulkSelectedUsersCount,
          }}
        />
      </M>
      <M>
        <Trans components={[<b />]} i18nKey={TRANSLATION_KEYS.messageText} />
      </M>
    </ConfirmationModal>
  );
};
